import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import logo2 from "../public/logo2.png";
import "./css/register.css";
import "./ui/theme.css";
import styles from "./Header.module.scss";
import { useTranslation } from "react-i18next";
import LanguageSelection from "../component/LanguageSelection";
import { useSelector, useDispatch } from "react-redux";
import useFetch from "./CustomHooks/useFetch";
import { logout } from "../Redux/actions/actionCreator/data.action";
import DropDownMenu from "../GeneralComponents/DropDownMenu";
import { useQueryClient } from "react-query";
import CCTVCommandCenter from "./CCTV/CCTVCommandCenter";
import { navigationOwnerForPath } from "./navigationOwnership";

const STEPSWATCH_PRODUCTS = [
	{
		name: "Web Builder",
		description: "Create and manage your public website.",
		href: "https://builder.stepswatch.co.in/",
		icon: "language",
	},
	{
		name: "Enterprise",
		description: "Access enterprise administration.",
		href: "https://enterprise.stepswatch.co.in/",
		icon: "business",
	},
	{
		name: "AI Command Center",
		description: "Use Stepswatch AI tools and assistance.",
		href: "https://qaai.stepswatch.co.in/",
		icon: "auto_awesome",
	},
	{
		name: "Center Management",
		description: "Manage your center's daily operations.",
		href: "https://center.stepswatch.co.in/",
		icon: "domain",
		current: true,
	},
	{
		name: "Parent-Teacher Application",
		description: "Connect parents and teachers from the Stepswatch mobile app.",
		icon: "phone_iphone",
		links: [
			{
				name: "App Store",
				href: "https://apps.apple.com/us/app/stepswatch-school-daycare/id6747612736",
			},
			{
				name: "Google Play",
				href: "https://play.google.com/store/apps/details?id=com.stepswatch",
			},
		],
	},
];

function Header() {
	const [activePill, setActivePill] = useState("childManagement");
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isProductSwitcherOpen, setIsProductSwitcherOpen] = useState(false);
	const [isCctvOpen, setIsCctvOpen] = useState(false);
	const allowedData = useSelector((state) => state.config).config || [];
	const location = useLocation();
	const history = useHistory();
	const dropdownRef = useRef(null);
	const productSwitcherRef = useRef(null);
	const { t } = useTranslation();

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsMenuOpen(false);
			}
			if (
				productSwitcherRef.current &&
				!productSwitcherRef.current.contains(event.target)
			) {
				setIsProductSwitcherOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		const owner = navigationOwnerForPath(location.pathname);
		if (owner) {
			setActivePill(owner);
			return;
		}
		const val = location.pathname.split("/")[1];
		if (val === "myDaycareNew") {
			setActivePill("daycare");
		} else if (val === "pnl") {
			setActivePill("reports");
		} else if (val === "staffmanagement") {
			setActivePill("staffManagement");
		} else if (val === "mealmanagement") {
			setActivePill("mealManagement");
		} else if (val === "activity") {
			setActivePill("activities");
		} else {
			setActivePill(val);
		}
	}, [location.pathname]);

	useEffect(() => {
		setIsMenuOpen(false);
		setIsProductSwitcherOpen(false);
	}, [location.pathname]);

	useFetch({
		url: "/admin/manager/editLabels",
		method: "POST",
		body: JSON.stringify({
			daycareManagerEmail: "contact@stepswatch.co.in",
			payload: {},
		}),
	});

	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const handleLogout = () => {
		queryClient.invalidateQueries();
		dispatch(logout());
		localStorage.removeItem("token");
		localStorage.removeItem("config");
	};

	const gotoProfile = () => {
		if (localStorage.getItem("accountType") !== "professional") {
			history.push("/myDaycareNew/details");
		} else {
			history.push("/staffmanagement/profile");
		}
	};

	const accountType = localStorage.getItem("accountType");
	const profileImage =
		accountType === "professional" &&
		localStorage.getItem("professionalPhoto") !== "undefined"
			? localStorage.getItem("professionalPhoto")
			: accountType === "daycare" &&
			  localStorage.getItem("daycarePhoto") !== "undefined"
				? localStorage.getItem("daycarePhoto")
				: null;

	const hasProfileImage = Boolean(profileImage);

	const handleModuleClick = (value) => {
		setActivePill(value);
		setIsMenuOpen(false);
	};

	return (
		<>
			<header className={styles.header} ref={dropdownRef}>
				<button
					type="button"
					className={styles.mobileToggle}
					onClick={() => setIsMenuOpen((state) => !state)}
					aria-label={t("Menu")}
					aria-expanded={isMenuOpen}
				>
					<span className="material-icons">{isMenuOpen ? "close" : "menu"}</span>
				</button>

				<div className={styles.productSwitcher} ref={productSwitcherRef}>
					<button
						type="button"
						className={`${styles.productSwitcherButton} ${
							isProductSwitcherOpen ? styles.productSwitcherButtonOpen : ""
						}`}
						onClick={() => {
							setIsMenuOpen(false);
							setIsProductSwitcherOpen((state) => !state);
						}}
						aria-label={t("Stepswatch products")}
						aria-expanded={isProductSwitcherOpen}
						aria-haspopup="menu"
						data-sw-tooltip={t("Stepswatch products")}
						data-sw-tooltip-position="bottom-start"
					>
						<span className="material-icons">apps</span>
					</button>

					{isProductSwitcherOpen ? (
						<div className={styles.productMenu} role="menu">
							<div className={styles.productMenuHeader}>
								<strong>{t("Stepswatch products")}</strong>
								<span>{t("Open another Stepswatch product")}</span>
							</div>
							<div className={styles.productList}>
								{STEPSWATCH_PRODUCTS.map((product) => {
									const productContent = <>
										<span className={styles.productIcon} aria-hidden="true">
											<span className="material-icons">{product.icon}</span>
										</span>
										<span className={styles.productDetails}>
											<span className={styles.productNameRow}>
												<strong>{product.name}</strong>
												{product.current ? (
													<small>{t("Current")}</small>
												) : null}
											</span>
											<span>{t(product.description)}</span>
										</span>
									</>;

									if (product.links) {
										return (
											<div
												key={product.name}
												className={`${styles.productItem} ${styles.productAppItem}`}
											>
												{productContent}
												<div className={styles.productStoreLinks}>
													{product.links.map((link) => (
														<a
															key={link.name}
															href={link.href}
															target="_blank"
															rel="noopener noreferrer"
															role="menuitem"
															onClick={() => setIsProductSwitcherOpen(false)}
														>
															{t(link.name)}
															<span className="material-icons" aria-hidden="true">open_in_new</span>
														</a>
													))}
												</div>
											</div>
										);
									}

									return (
										<a
											key={product.name}
											href={product.href}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.productItem}
											role="menuitem"
											onClick={() => setIsProductSwitcherOpen(false)}
										>
											{productContent}
											<span className="material-icons" aria-hidden="true">open_in_new</span>
										</a>
									);
								})}
							</div>
						</div>
					) : null}
				</div>

				<Link to="/" className={styles.brand} onClick={() => setIsMenuOpen(false)}>
					<img src={logo2} alt="Stepswatch" />
				</Link>

				<nav
					className={`${styles.moduleNav} ${
						isMenuOpen ? styles.moduleNavOpen : ""
					}`}
					aria-label={t("Modules")}
				>
					{allowedData.map((ele, index) => {
						const isActive = activePill === ele.value;

						if (ele.type) {
							return (
								<div className={styles.moduleDropdown} key={index}>
									<button
										type="button"
										className={`${styles.moduleButton} ${
											isActive ? styles.activeModule : ""
										}`}
										onClick={() => setActivePill(ele.value)}
									>
										{t(`${ele.name}`)}
										<span className="material-icons">expand_more</span>
									</button>
									<div className={styles.dropdownMenu}>
										{ele.data.map((dropdown, dropdownIndex) => (
											<NavLink
												key={dropdownIndex}
												to={dropdown.path}
												className={styles.dropdownItem}
												onClick={() => handleModuleClick(ele.value)}
											>
												{t(dropdown.name)}
											</NavLink>
										))}
									</div>
								</div>
							);
						}

						return (
							<NavLink
								key={index}
								to={ele.path}
								className={`${styles.moduleLink} ${
									isActive ? styles.activeModule : ""
								}`}
								onClick={() => handleModuleClick(ele.value)}
							>
								{t(`${ele.name}`)}
							</NavLink>
						);
					})}
				</nav>

				<div className={styles.rightTools}>
					<span
						data-sw-tooltip={t("CCTV feeds")}
						data-sw-tooltip-position="bottom"
						className={styles.tooltipWrap}
					>
						<button
							type="button"
							className={styles.cctvButton}
							onClick={() => {
								setIsMenuOpen(false);
								setIsCctvOpen(true);
							}}
							aria-label={t("Open CCTV feeds")}
						>
							<span className="material-icons">videocam</span>
						</button>
					</span>
					<LanguageSelection />
					<DropDownMenu
						options={[
							{ name: `${t("profile")}`, click: gotoProfile },
							{ name: `${t("logout")}`, click: handleLogout },
						]}
						img={profileImage}
						imageVariant={accountType === "daycare" ? "logo" : "avatar"}
						icon={!hasProfileImage ? "user-circle" : null}
					/>
				</div>
			</header>

			<CCTVCommandCenter
				isOpen={isCctvOpen}
				onClose={() => setIsCctvOpen(false)}
			/>
		</>
	);
}

export default Header;
